import React from 'react'
import Layout from 'components/layout'

const SecurityOverview = () => {
  return (
    <Layout>
      <h2>Security Overview</h2>
      <p>
        <strong>Toast Ninja Inc.</strong>
        <br />
        Last reviewed: June 2026
        <br />
        Contact: <a href="mailto:support@toast.ninja">support@toast.ninja</a>
      </p>
      <h3>What Toast Does</h3>
      <p>
        Toast is a GitHub and Slack integration that delivers pull request notifications and team activity summaries to
        Slack. It listens to GitHub webhook events, processes metadata about pull request activity, and sends formatted
        messages to Slack channels and users.
      </p>
      <p>
        Toast does not provide code hosting, code execution, file storage, or any service that requires access to the
        contents of your repositories.
      </p>
      <h3>What Data Toast Accesses</h3>
      <h4>What we access</h4>
      <p>
        When you install Toast, you authorize it through GitHub's and Slack's standard OAuth flows. Through those
        authorizations, Toast accesses:
      </p>
      <ul>
        <li>
          <strong>From GitHub:</strong> Pull request titles, PR numbers, open/closed/merged status, review states
          (approved, changes requested, commented), reviewer assignments, author names, usernames, and profile photos.
          Organization name, team names, and member lists.
        </li>
        <li>
          <strong>From Slack:</strong> Workspace name, channel IDs, and user IDs sufficient to deliver notifications
          to the right channels and people. Toast receives messages you explicitly send to it: slash commands (such as{' '}
          <strong>/toast</strong>), button interactions within Toast messages, and content you provide when using Toast
          to post a comment to GitHub on your behalf.
        </li>
        <li>
          <strong>Account information:</strong> Names, usernames, and email addresses from GitHub and Slack OAuth,
          used to associate your Toast configuration with your account.
        </li>
        <li>
          <strong>Billing:</strong> Your organization's subscription plan and Stripe customer ID. We do not handle or
          store payment card numbers.
        </li>
      </ul>
      <h4>What we do not access</h4>
      <ul>
        <li>
          <strong>Source code.</strong> Toast does not request, receive, or store the contents of any file in any
          repository. The GitHub App permissions we request explicitly exclude repository contents.
        </li>
        <li>
          <strong>Issue or PR body text.</strong> We process the existence and state of pull requests, not their
          descriptions or comment bodies.
        </li>
        <li>
          <strong>Slack message history.</strong> We do not read messages in channels or conversations where Toast has
          not been explicitly addressed.
        </li>
        <li>
          <strong>Credentials or secrets.</strong> We never see GitHub personal access tokens, SSH keys, CI/CD
          secrets, or any secrets stored in your repositories or CI environment.
        </li>
      </ul>
      <p>
        This access model is intentional and architectural. Because Toast operates on metadata rather than content, a
        compromise of Toast's systems would not expose your source code, your intellectual property, or sensitive
        information that might be present in code or issue descriptions.
      </p>
      <h3>Infrastructure and Hosting</h3>
      <p>
        Toast's production infrastructure runs entirely on cloud services operated by established,
        compliance-certified providers. Toast does not operate any physical servers, data centers, or networking
        equipment.
      </p>
      <table>
        <thead>
          <tr>
            <th>Component</th>
            <th>Provider</th>
            <th>Location</th>
            <th>Provider Compliance</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Application hosting</td>
            <td>Heroku (Salesforce)</td>
            <td>US (AWS us-east-1)</td>
            <td>SOC 2 Type II, ISO 27001</td>
          </tr>
          <tr>
            <td>PostgreSQL database</td>
            <td>Heroku Postgres</td>
            <td>US (AWS us-east-1)</td>
            <td>Covered by Heroku SOC 2</td>
          </tr>
          <tr>
            <td>Message queue</td>
            <td>CloudAMQP</td>
            <td>US</td>
            <td>SOC 2 Type II</td>
          </tr>
          <tr>
            <td>Cache (Redis)</td>
            <td>Redis Cloud</td>
            <td>US</td>
            <td>SOC 2 Type II</td>
          </tr>
          <tr>
            <td>Error monitoring</td>
            <td>Sentry</td>
            <td>US</td>
            <td>SOC 2 Type II</td>
          </tr>
          <tr>
            <td>Payment processing</td>
            <td>Stripe</td>
            <td>US</td>
            <td>PCI DSS Level 1</td>
          </tr>
        </tbody>
      </table>
      <p>All production data is stored and processed in the United States.</p>
      <h3>Authentication and Access Control</h3>
      <p>
        User authentication is handled entirely through OAuth 2.0 via GitHub and Slack. Toast does not implement its
        own username and password system and does not store user passwords.
      </p>
      <p>
        GitHub App installation grants scoped, revocable API access. Customers can revoke Toast's access at any time
        from GitHub's organization settings, which immediately terminates all API access. Slack App installation uses
        OAuth 2.0 workspace authorization and can similarly be revoked at any time.
      </p>
      <p>
        All inbound webhooks from GitHub and Slack are verified using HMAC signature validation before processing.
        Stripe webhook payloads are verified using Stripe's webhook signature scheme.
      </p>
      <h3>Data Security</h3>
      <ul>
        <li>
          <strong>Encryption in transit:</strong> All communication between Toast and external services uses TLS 1.2
          or higher. Webhooks are received over HTTPS only.
        </li>
        <li>
          <strong>Encryption at rest:</strong> Heroku Postgres encrypts data at rest on the underlying AWS EBS
          volumes using AES-256.
        </li>
        <li>
          <strong>OAuth token storage:</strong> GitHub and Slack OAuth tokens are stored in the production database.
          They are not logged or included in error reports.
        </li>
        <li>
          <strong>Secrets management:</strong> API keys and signing secrets are stored as environment variables in
          Heroku's encrypted configuration system, not in source code.
        </li>
        <li>
          <strong>Error monitoring:</strong> Sensitive headers and authentication tokens are explicitly redacted
          before error payloads are transmitted to Sentry.
        </li>
      </ul>
      <h3>Subprocessors</h3>
      <p>
        Toast uses the following third-party subprocessors that may process customer personal data as part of
        delivering the service:
      </p>
      <table>
        <thead>
          <tr>
            <th>Subprocessor</th>
            <th>Service</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Heroku (Salesforce, Inc.)</td>
            <td>Application hosting and managed PostgreSQL</td>
            <td>US</td>
          </tr>
          <tr>
            <td>CloudAMQP (84codes AB)</td>
            <td>Managed message queue</td>
            <td>US</td>
          </tr>
          <tr>
            <td>Redis Cloud (Redis Ltd.)</td>
            <td>Managed cache</td>
            <td>US</td>
          </tr>
          <tr>
            <td>Sentry (Functional Software, Inc.)</td>
            <td>Error monitoring and performance tracking</td>
            <td>US</td>
          </tr>
          <tr>
            <td>Stripe, Inc.</td>
            <td>Payment processing (billing data only)</td>
            <td>US</td>
          </tr>
        </tbody>
      </table>
      <p>
        We will notify customers of material changes to this subprocessor list with reasonable advance notice. A Data
        Processing Addendum (DPA) is available on request at{' '}
        <a href="mailto:support@toast.ninja">support@toast.ninja</a>.
      </p>
      <h3>Incident Response</h3>
      <p>
        Toast commits to notifying affected customers within 72 hours of becoming aware of a confirmed incident
        involving unauthorized access to personal data, consistent with GDPR Article 33 obligations. Notification
        will include the nature of the incident, the categories and approximate volume of data affected, and the
        measures taken to address it.
      </p>
      <p>
        To report a suspected security vulnerability or incident, contact:{' '}
        <a href="mailto:support@toast.ninja">support@toast.ninja</a>
      </p>
      <h3>Business Continuity</h3>
      <p>
        Toast's service continuity relies on the redundancy and backup capabilities of its infrastructure providers.
        Heroku Postgres provides continuous WAL archiving and point-in-time recovery, with daily backups retained
        automatically. The application can be redeployed from source within approximately 30 minutes.
      </p>
      <p>
        Recovery Time Objective (RTO): 4 hours (best effort).
        <br />
        Recovery Point Objective (RPO): 24 hours, or shorter with point-in-time recovery.
      </p>
      <h3>Compliance Notes</h3>
      <ul>
        <li>
          <strong>SOC 2:</strong> Toast has not yet completed a SOC 2 audit. We rely on the SOC 2 certifications of
          our infrastructure providers as the primary assurance framework for infrastructure-level controls.
        </li>
        <li>
          <strong>Penetration testing:</strong> Toast has not conducted a third-party penetration test. We assess this
          risk as reduced by our narrow access surface: no code access, no file storage, OAuth-only authentication,
          and signature-verified inbound webhooks.
        </li>
        <li>
          <strong>GDPR:</strong> Toast offers a Data Processing Addendum for customers who require one. Contact{' '}
          <a href="mailto:support@toast.ninja">support@toast.ninja</a> to request a DPA.
        </li>
        <li>
          <strong>PCI DSS:</strong> Toast does not store, process, or transmit payment card data. All payment
          processing is handled by Stripe (PCI DSS Level 1 certified).
        </li>
      </ul>
      <h3>Frequently Asked Questions</h3>
      <h4>Can Toast read our source code?</h4>
      <p>
        No. The GitHub App permissions Toast requests explicitly exclude repository contents. We receive pull request
        metadata (titles, states, reviewer assignments) but not diffs, file contents, or any repository content.
        This is enforced at the GitHub API permission level, not just by policy.
      </p>
      <h4>What GitHub permissions does Toast request, and why?</h4>
      <table>
        <thead>
          <tr>
            <th>Permission</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Pull requests (read)</td>
            <td>To receive PR events and read PR metadata</td>
          </tr>
          <tr>
            <td>Members (read)</td>
            <td>To map GitHub users to Slack users within your org</td>
          </tr>
          <tr>
            <td>Metadata (read)</td>
            <td>Required by GitHub for all App installations</td>
          </tr>
          <tr>
            <td>Statuses / Checks (read)</td>
            <td>To report CI check states on PRs</td>
          </tr>
          <tr>
            <td>Emails (read)</td>
            <td>To match GitHub accounts to Slack accounts</td>
          </tr>
        </tbody>
      </table>
      <p>
        Toast does not request: <strong>contents</strong> (code), <strong>administration</strong>,{' '}
        <strong>secrets</strong>, or any write permission to your repositories.
      </p>
      <h4>What Slack permissions does Toast request, and why?</h4>
      <table>
        <thead>
          <tr>
            <th>Permission</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>chat:write</td>
            <td>To send PR notification messages</td>
          </tr>
          <tr>
            <td>users:read</td>
            <td>To look up Slack users by email for notification routing</td>
          </tr>
          <tr>
            <td>channels:read</td>
            <td>To let you select which channel receives notifications</td>
          </tr>
          <tr>
            <td>commands</td>
            <td>To support /toast slash commands</td>
          </tr>
        </tbody>
      </table>
      <p>
        Toast does not request access to read message history, access private conversations, or any administrative
        Slack permissions.
      </p>
      <h4>Who at Toast can see our data?</h4>
      <p>
        A small number of Toast personnel with authorized access to the production environment can access account data
        for support and operational purposes. We do not access customer data unless required to resolve a support
        issue, and we do not sell or share data with third parties for advertising or analytics purposes.
      </p>
      <h4>What happens to our data when we cancel or uninstall?</h4>
      <p>
        When you uninstall the Toast GitHub App or Slack App, we stop receiving new data immediately. On request, we
        will delete your organization's data from our database. Contact{' '}
        <a href="mailto:support@toast.ninja">support@toast.ninja</a> to request deletion.
      </p>
      <h4>Do you sell our data?</h4>
      <p>
        No. Toast does not sell, rent, or share customer data with third parties for advertising, marketing, or any
        commercial purpose unrelated to delivering the Toast service.
      </p>
      <h4>Are you GDPR compliant?</h4>
      <p>
        Toast processes personal data (names, usernames, email addresses) on behalf of customers who may be subject to
        GDPR. We offer a Data Processing Addendum (DPA) that governs our obligations as a data processor. Contact{' '}
        <a href="mailto:support@toast.ninja">support@toast.ninja</a> to request a DPA.
      </p>
      <h4>Have you had any security incidents?</h4>
      <p>No security incidents involving unauthorized access to customer data have occurred.</p>
      <h4>Can we get a copy of this document for our records?</h4>
      <p>
        Yes. Contact <a href="mailto:support@toast.ninja">support@toast.ninja</a> for a signed PDF version or to
        request our Data Processing Addendum.
      </p>
    </Layout>
  )
}

export default SecurityOverview
